import {
  authStore,
  authReducer,
  setCredentials,
  setCurrentUser,
  setNotificationSettingsState,
  updateNotificationSettings,
  setUserActivityTimestampState,
  selectUserIsActivated,
  selectToken,
  selectUsername,
  selectCurrentUser,
  selectUserFullName,
  selectNotificationSettings,
  selectFirstName,
  selectFirstLetterOfName,
  selectCurrentUserAvatar,
  selectCurrentUserLoaded,
  selectIsLoggedIn,
  selectActivityTimestamps,
  selectProfileComplete,
  useLazyCurrentUserQuery,
  getNavigator,
} from '@gusiberi/shared'
import type {
  AuthState,
  NotificationSettings,
  User,
  UserActivityTimestamp,
} from '@gusiberi/shared'
import { useDispatch, useSelector } from 'react-redux'
import { useUiStore } from './uiStore'
import Auth from '../utils/auth'

export {
  authStore,
  authReducer,
  setCredentials,
  setCurrentUser,
  setNotificationSettingsState,
  updateNotificationSettings,
  setUserActivityTimestampState,
  selectUserIsActivated,
  selectToken,
  selectUsername,
  selectCurrentUser,
  selectUserFullName,
  selectNotificationSettings,
  selectFirstName,
  selectFirstLetterOfName,
  selectCurrentUserAvatar,
  selectCurrentUserLoaded,
  selectIsLoggedIn,
  selectActivityTimestamps,
  selectProfileComplete,
}
export type { AuthState }

export const useAuthStore = () => {
  const dispatch = useDispatch()
  const { closeLoadingScreen, openLoadingScreen } = useUiStore()
  const currentUser = useSelector(selectCurrentUser)
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const currentUserLoaded = useSelector(selectCurrentUserLoaded)
  const notificationSettings = useSelector(selectNotificationSettings)
  const userIsActivated = useSelector(selectUserIsActivated)
  const activityTimestamps = useSelector(selectActivityTimestamps)
  const [fetchUserDetails, { isLoading: isLoadingUser, isError: failedToLoadUser }] =
    useLazyCurrentUserQuery({})

  const setUser = (user: User) => {
    dispatch(setCurrentUser(user))
  }

  const setNotificationSettings = (notification: NotificationSettings) => {
    dispatch(setNotificationSettingsState(notification))
  }

  const setUserActivityTimestamps = (activityTimestamps: UserActivityTimestamp) => {
    dispatch(setUserActivityTimestampState(activityTimestamps))
  }

  const loadUser = async () => {
    if (isLoadingUser) return {}
    try {
      const userDetails = await fetchUserDetails({}).unwrap()
      setUser(userDetails.payload?.user)
      setNotificationSettings(userDetails.payload?.notification_settings)
      setUserActivityTimestamps(userDetails.payload?.activities)
      return { userDetails: userDetails.payload }
    } catch {
      return {}
    }
  }

  const logUserOut = () => {
    openLoadingScreen('Logging out!')
    Auth.logUserOut()
    setTimeout(() => {
      closeLoadingScreen()
      getNavigator().goToAuth()
    }, 1000)
  }

  const loadUserIfNecessary = async () => {
    if (!isLoggedIn && Auth.isLoggedIn()) {
      await loadUser()
    }
  }

  return {
    currentUser,
    isLoggedIn,
    currentUserLoaded,
    userIsActivated,
    loadUser,
    loadUserIfNecessary,
    logUserOut,
    failedToLoadUser,
    isLoadingUser,
    notificationSettings,
    activityTimestamps,
    setUser,
    setNotificationSettings,
    setUserActivityTimestamps,
  }
}
